/*
    THE ROUND OF EVERY LIVE GAME — WHAT IT DRAWS OF ITS OWN

    RoundView holds the concept every live game shares. Everything a single game does differently
    lives here and nowhere else: the rows of cards it deals, the names of its bets, the winner it
    announces and the buttons its round still answers to.
*/

/* REST API */
import * as MultiPoker from "../../api/MultiPoker"
import * as Blackjack from "../../api/Blackjack"
import * as PuntoBanco from "../../api/PuntoBanco"
import * as VipThreeBox from "../../api/VipThreeBox"
import * as UltimateThreeHand from "../../api/UltimateThreeHand"
import * as NiuNiuApi from "../../api/NiuNiu"
import * as Roulette from "../../api/Roulette"

/* Translate */
import { t } from "../../translates/translate"


/* THE BUTTONS ALMOST EVERY LIVE ROUND CARRIES */
const ACTIONS = {
    end: true,
    refundAll: true,
    pause: true,
    revoke: true,
    continue: true,
}


/* THE CARDS OF A PLAYER INSIDE THE PLAYER CARD */
const PLAYER_CARDS = [
    { title: () => t("round.dealerCards"), type: "dealer" },
    { title: () => t("round.playerCards"), type: "player" },
]


/* MULTI PLAYER POKER — ONE HAND FOR THE WHOLE TABLE */
const multi = {
    api: MultiPoker,
    cards: [
        { title: () => t("round.dealerCards"), type: "dealer" },
        { title: () => t("round.additionalCards"), type: "additional" },
        { title: () => t("round.playerCards"), type: "player" },
        { title: () => t("round.buyCards"), type: "buy", optional: true },
    ],
    playerCards: PLAYER_CARDS,
    bets: "process",
    actions: ACTIONS,
}


/* ONE BLACKJACK — THE DEALER SERVER WAITS FOR THE ROUND INSIDE THE PAUSE EVENT */
const blackjack = {
    api: Blackjack,
    cards: [
        { title: () => t("round.dealerCards"), type: "dealer" },
        { title: () => t("round.playerCards"), type: "player" },
    ],
    playerCards: PLAYER_CARDS,
    bets: "process",
    actions: ACTIONS,
    pauseGameID: true,
}


/* PUNTO BANCO — THE BANKER INSTEAD OF THE DEALER, AND THE BURNED CARDS OF THE SHOE */
const punto = {
    api: PuntoBanco,
    cards: [
        { title: () => t("round.bankerCards"), type: "banker" },
        { title: () => t("round.playerCards"), type: "player" },
        { title: () => t("round.burnedCards"), type: "burn", optional: true },
    ],
    playerCards: [
        { title: () => t("round.bankerCards"), type: "banker" },
        { title: () => t("round.playerCards"), type: "player" },
    ],
    bets: "process",
    betNames: {
        player: "PLAYER",
        banker: "BANKER",
        tie: "TIE",
        playerPair: "PLAYER PAIR",
        bankerPair: "BANKER PAIR",
        perfectPair: "PERFECT PAIR",
        eitherPair: "EITHER PAIR",
        refund: "REFUND",
    },
    win: "punto",
    actions: ACTIONS,
}


/* VIP THREE BOX — ONE PLAYER PER BOX, ONE ROW OF CARDS PER BOX, AND THE BOARD SHARED BY ALL OF THEM */
const vipThreeBox = {
    api: VipThreeBox,
    cards: [
        { title: () => t("round.dealerCards"), type: "dealer" },
        { title: () => t("round.additionalCards"), type: "additional", optional: true },
        { title: () => `${t("round.box")} 1`, type: "player", box: 1 },
        { title: () => `${t("round.box")} 2`, type: "player", box: 2 },
        { title: () => `${t("round.box")} 3`, type: "player", box: 3 },
    ],
    cardsClass: "vip-three-box",
    /* The player card holds only the boxes this very player sat at — the empty rows fall away */
    playerCards: [
        { title: () => t("round.dealerCards"), type: "dealer" },
        { title: () => t("round.additionalCards"), type: "additional", optional: true },
        { title: () => `${t("round.box")} 1`, type: "player", box: 1, optional: true },
        { title: () => `${t("round.box")} 2`, type: "player", box: 2, optional: true },
        { title: () => `${t("round.box")} 3`, type: "player", box: 3, optional: true },
    ],
    playerCardsClass: "vip-three-box",
    bets: "process",
    actions: ACTIONS,
}


/* ULTIMATE THREE BOX — THE THREE BOXES BELONG TO NOBODY: EVERY PLAYER MAY HOLD ALL THREE AT ONCE,
   AND THE SAME BOX CARRIES THE STAKES OF EVERYONE WHO BET ON IT. SO A ROW OF THE PLAYER LIST IS
   A BET ON A BOX, NOT A SEAT, AND THE THREE HANDS ARE ALWAYS DEALT AND ALWAYS SHOWN. */
const ultimateThreeHand = {
    api: UltimateThreeHand,
    cards: [
        { title: () => t("round.dealerCards"), type: "dealer" },
        { title: () => t("round.additionalCards"), type: "additional", optional: true },
        { title: () => `${t("round.box")} 1`, type: "player", box: 1 },
        { title: () => `${t("round.box")} 2`, type: "player", box: 2 },
        { title: () => `${t("round.box")} 3`, type: "player", box: 3 },
    ],
    cardsClass: "three-box",
    /* The player card keeps only the boxes this very player bet on — the rest fall away */
    playerCards: [
        { title: () => t("round.dealerCards"), type: "dealer" },
        { title: () => t("round.additionalCards"), type: "additional", optional: true },
        { title: () => `${t("round.box")} 1`, type: "player", box: 1, optional: true },
        { title: () => `${t("round.box")} 2`, type: "player", box: 2, optional: true },
        { title: () => `${t("round.box")} 3`, type: "player", box: 3, optional: true },
    ],
    playerCardsClass: "three-box",
    bets: "process",
    actions: ACTIONS,
}


/* NIU NIU — THREE SHARED BOXES OF FIVE CARDS, AND NOTHING ASKED OF THE PLAYER ONCE THEY ARE OUT.

   The cloth carries one extra card that belongs to no hand: the position card, pulled before
   the deal and counted round the table to say which box it starts at. Three independent
   stakes ride on every box, and each is settled on its own against the dealer's five. */
const niuNiu = {
    api: NiuNiuApi,
    cards: [
        { title: () => t("round.positionCard"), type: "position", optional: true },
        { title: () => t("round.dealerCards"), type: "dealer" },
        { title: () => `${t("round.box")} 1`, type: "player", box: 1 },
        { title: () => `${t("round.box")} 2`, type: "player", box: 2 },
        { title: () => `${t("round.box")} 3`, type: "player", box: 3 },
    ],
    cardsClass: "three-box",
    /* The player card keeps only the boxes this very player bet on — the rest fall away */
    playerCards: [
        { title: () => t("round.positionCard"), type: "position", optional: true },
        { title: () => t("round.dealerCards"), type: "dealer" },
        { title: () => `${t("round.box")} 1`, type: "player", box: 1, optional: true },
        { title: () => `${t("round.box")} 2`, type: "player", box: 2, optional: true },
        { title: () => `${t("round.box")} 3`, type: "player", box: 3, optional: true },
    ],
    playerCardsClass: "three-box",
    bets: "process",
    betNames: {
        bet: "NORMAL BET",
        double: "DOUBLE BET",
        super: "SUPER DOUBLE",
        maxpay: "MAXPAY",
        refund: "REFUND",
    },
    actions: ACTIONS,
}


/* ROULETTE — NO CARDS AT ALL, EVERY STAKE OF A PLAYER IN ONE COLUMN, AND THE WINS GO OUT BY HAND */
const roulette = {
    api: Roulette,
    cards: [],
    playerCards: [],
    bets: "stakes",
    win: "roulette",
    continueText: () => t("widget.continueGame"),
    /* The wheel may give the number after the dealer paused — the finished round is continued to free the table */
    continueFinished: true,
    actions: {
        end: true,
        refundAll: true,
        refundAndPayAll: true,
        pause: true,
        revoke: false,
        continue: true,
    },
}


/* THE GAMES OF THE CATALOGUE — Tables.game IS THE ONLY KEY */
const GAMES = {

    /* Multi Player Poker */
    "russian-multipoker": { ...multi, postfix: "RMP" },
    "lunar-multipoker": { ...multi, postfix: "LMP" },
    "novo-multipoker": { ...multi, postfix: "NMP" },
    "open-multipoker": { ...multi, postfix: "OMP" },
    "queen-multipoker": { ...multi, postfix: "QMP" },
    "european-multipoker": { ...multi, postfix: "EMP" },
    "ultimate-multipoker": { ...multi, postfix: "UMP" },
    "holdem-multipoker": { ...multi, postfix: "HMP" },

    /* Blackjack */
    "one-blackjack": { ...blackjack, postfix: "MOB" },

    /* Punto Banco */
    "punto-banco": { ...punto, postfix: "MPB" },

    /* Vip Three Box */
    "vip-ultimate-texas-holdem-vip": { ...vipThreeBox, postfix: "UVP" },

    /* Ultimate Three Box — the multiplayer table played on three shared boxes */
    "ultimate-three-hand-multipoker": { ...ultimateThreeHand, postfix: "TUMP" },

    /* Niu Niu — three shared boxes, five cards each, three independent stakes per box */
    "niu-niu-multipoker": { ...niuNiu, postfix: "NIU" },

    /* Roulette */
    "roulette-1": { ...roulette, postfix: "RO1" },
}


/* THE ROUND OF ONE GAME OF THE CATALOGUE — null WHEN THE PANEL DRAWS IT ITS OWN WAY */
const find = (key) => GAMES[`${key}`.trim()] ? GAMES[`${key}`.trim()] : null


export { multi, blackjack, punto, vipThreeBox, ultimateThreeHand, niuNiu, roulette, find }
